import { useTranslation } from "react-i18next";
import { useLoader } from "../../../_helper/LoaderContext";
import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { getStaffShiftHistoryDetails } from "../service/manage-staff-service";
import { Breadcrumbs } from "../../../AbstractElements";
import { Card, CardBody, CardHeader, Col, Row, Badge, Table } from "reactstrap";
import { ArrowLeftCircle, Clock, DollarSign, User, Info, Calendar, AlertCircle, CreditCard } from "react-feather";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useCurrency } from "../../../_helper/CurrencyContext";
import { usePharmacy } from "../../../_helper/PharmacyProvider";
import S3ImageLoader from "../../../S3ImageLoader";
import errorImage from "../../../assets/images/blank_profile.png"
import moment from "moment";

const ShiftRecordDetails = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const { getCurrencySymbol, allowedCurrencies } = useCurrency();
    const [skeletonLoading, setSkeletonLoading] = useState(true);
    const { recordId } = useParams();
    const navigate = useNavigate();
    const [shiftData, setShiftData] = useState(null);
    const { pharmacyPOS } = usePharmacy();

    useEffect(() => {
        if (!recordId) {
            navigate(`${process.env.PUBLIC_URL}/shift-record`);
        } else {
            fetchShiftDetails();
        }
    }, [recordId]);

    const fetchShiftDetails = async () => {
        try {
            setLoading(true);
            setSkeletonLoading(true);
            const requestBody = { shiftHistoryId: recordId };
            const response = await getStaffShiftHistoryDetails(requestBody);

            if (response.data.status === 'success') {
                setShiftData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching shift details:', error);
        } finally {
            setLoading(false);
            setSkeletonLoading(false);
        }
    };

    const handleBack = () => {
        navigate(`${process.env.PUBLIC_URL}/shift-record`);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return moment(date).format('DD MMM YYYY HH:mm:ss');
    };

    // Updated formatCurrency to be dynamic
    const formatCurrency = (amount, currencyCode = "INR") => {
        if (amount === undefined || amount === null) return `${getCurrencySymbol(currencyCode)} 0.00`;

        // If it's a string status like "equal", "short", "excess"
        if (typeof amount === 'string' && isNaN(parseFloat(amount))) {
            return t(amount.charAt(0).toUpperCase() + amount.slice(1));
        }

        const numericAmount = parseFloat(amount);
        if (isNaN(numericAmount)) return amount;

        const formattedAmount = new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numericAmount);

        // Use dynamic symbol based on currency code
        const symbol = getCurrencySymbol(currencyCode);

        // Check if we should use prefix or suffix (ZiG uses suffix)
        if (currencyCode === "ZiG") {
            return `${formattedAmount}${symbol}`;
        }

        return `${symbol} ${formattedAmount}`;
    };

    // Helper to display all currencies from an amount object
    const renderAllCurrencies = (amountObj) => {
        if (!amountObj || typeof amountObj !== 'object' || Object.keys(amountObj).length === 0) {
            return null;
        }

        return (
            <div className="currency-list">
                {Object.entries(amountObj).map(([currencyCode, amount]) => (
                    <div key={currencyCode} className="currency-item mb-1">
                        {formatCurrency(amount, currencyCode)}
                    </div>
                ))}
            </div>
        );
    };

    // New helper for Cash Difference with badges
    const renderCashDifference = (differenceObj) => {
        if (!differenceObj || typeof differenceObj !== 'object' || Object.keys(differenceObj).length === 0) {
            return null;
        }

        return (
            <div className="currency-list">
                {Object.entries(differenceObj).map(([currencyCode, status]) => {
                    let badgeColor = 'secondary';
                    let label = String(status);

                    if (typeof status === 'string') {
                        const lowerStatus = status.toLowerCase();
                        if (lowerStatus === 'short') badgeColor = 'danger';
                        else if (lowerStatus === 'excess' || lowerStatus === 'surplus') badgeColor = 'success';
                        else if (lowerStatus === 'equal') badgeColor = 'light';

                        label = t(status.charAt(0).toUpperCase() + status.slice(1));
                    }

                    return (
                        <div key={currencyCode} className="currency-item mb-1 d-flex align-items-center">
                            <span className="me-2 small text-muted">{currencyCode}:</span>
                            <Badge color={badgeColor} pill className={badgeColor === 'light' ? 'text-dark border' : ''}>
                                {label}
                            </Badge>
                        </div>
                    );
                })}
            </div>
        );
    };


    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'ended':
                return <Badge color="danger" pill>{t('Ended')}</Badge>;
            case 'active':
                return <Badge color="light" pill>{t('Active')}</Badge>;
            default:
                return <Badge color="warning" pill>{status || t('Unknown')}</Badge>;
        }
    };

    const renderSkeleton = () => (
        <div className="skeleton-container">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="mb-3">
                    <Skeleton height={30} />
                </div>
            ))}
        </div>
    );

    if (skeletonLoading) {
        return (
            <Fragment>
                <Breadcrumbs mainTitle={t('Staff Shift History Detail')} parent={t('Dashboard')} title={t('Staff Shift History Detail')} />
                <div className="container mt-4">
                    <Card>
                        <CardHeader>
                            <Skeleton height={40} />
                        </CardHeader>
                        <CardBody>
                            {renderSkeleton()}
                        </CardBody>
                    </Card>
                </div>
            </Fragment>
        );
    }

    if (!shiftData) {
        return (
            <Fragment>
                <Breadcrumbs mainTitle={t('Staff Shift History Detail')} parent={t('Dashboard')} title={t('Staff Shift History Detail')} />
                <div className="container mt-4">
                    <Card>
                        <CardBody className="text-center py-5">
                            <AlertCircle size={48} className="text-muted mb-3" />
                            <h5>{t('No shift data found')}</h5>
                            <button
                                className="btn btn-primary d-flex align-items-center"
                                style={{ color: 'white', height: "40px" }}
                                to=""
                                onClick={handleBack}>
                                <ArrowLeftCircle className="me-2" /> {t("Back")}
                            </button>
                        </CardBody>
                    </Card>
                </div>
            </Fragment>
        );
    }

    const { shiftHistory, cashierInfo } = shiftData;

    return (
        <Fragment>
            <Breadcrumbs
                mainTitle={t('Staff Shift History Detail')}
                parent={t('Dashboard')}
                title={t('Staff Shift History Detail')}
            />

            <div className="container mt-4">
                <div className="d-flex mb-3 justify-content-end">
                    <button
                        className="btn btn-primary d-flex align-items-center"
                        style={{ color: 'white', height: "40px" }}
                        to=""
                        onClick={handleBack}>
                        <ArrowLeftCircle className="me-2" /> {t("Back")}
                    </button>
                </div>

                <Row>
                    {/* Shift Overview Card */}
                    <Col lg={8}>
                        <Card className="mb-4 shadow-sm">
                            <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 d-flex align-items-center">
                                    <Clock className="me-2" size={20} />
                                    {t('Shift Overview')}
                                </h6>
                                {getStatusBadge(shiftHistory.status)}
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    {/* <Col md={6}>
                                        <div className="shift-info-card mb-3">
                                            <small className="text-muted d-block">{t('Shift ID')}</small>
                                            <p className="mb-0 fw-bold text-truncate" title={shiftHistory._id}>
                                                {shiftHistory._id}
                                            </p>
                                        </div>
                                    </Col> */}
                                    <Col md={6}>
                                        <div className="shift-info-card mb-3">
                                            <small className="text-muted d-block">{t('Duration')}</small>
                                            <p className="mb-0 fw-bold">
                                                {formatDateTime(shiftHistory.shiftStartTime)} -
                                                {shiftHistory.shiftEndTime ? formatDateTime(shiftHistory.shiftEndTime) : t('Ongoing')}
                                            </p>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="mt-3">
                                    <Col md={6} lg={4}>
                                        <div className="stat-card text-center p-3 border rounded mb-3">
                                            <DollarSign className="text-success mb-2" size={24} />
                                            <h4 className="mb-1">
                                                {renderAllCurrencies(shiftHistory.openingAmount)}
                                            </h4>
                                            <small className="text-muted">{t('Opening Amount')}</small>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <div className="stat-card text-center p-3 border rounded mb-3">
                                            <CreditCard className="text-info mb-2" size={24} />
                                            <h4 className="mb-1">
                                                {renderAllCurrencies(shiftHistory.totalSalesSystemCalculatedAmount)}
                                            </h4>
                                            <small className="text-muted">{t('System Calculated')}</small>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <div className="stat-card text-center p-3 border rounded mb-3">
                                            <Info className="text-warning mb-2" size={24} />
                                            <h4 className="mb-1">
                                                {shiftHistory.totalSalesCount || 0}
                                            </h4>
                                            <small className="text-muted">{t('Total Sales')}</small>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <div className="stat-card text-center p-3 border rounded mb-3">
                                            <CreditCard className="text-danger mb-2" size={24} />
                                            <h4 className="mb-1">
                                                {renderAllCurrencies(shiftHistory.closingAmount)}
                                            </h4>
                                            <small className="text-muted">{t('Closing Amount')}</small>
                                        </div>
                                    </Col>
                                    {/* <Col md={6} lg={4}>
                                        <div className="stat-card text-center p-3 border rounded mb-3">
                                            <DollarSign className="text-primary mb-2" size={24} />
                                            <h4 className="mb-1">
                                                {renderAllCurrencies(shiftHistory.totalSalesAmount)}
                                            </h4>
                                            <small className="text-muted">{t('Total Sales Amount')}</small>
                                        </div>
                                    </Col> */}
                                    <Col md={6} lg={4}>
                                        <div className="stat-card text-center p-3 border rounded mb-3">
                                            <AlertCircle className="text-secondary mb-2" size={24} />
                                            <h4 className="mb-1">
                                                {shiftHistory.totalRefundCount || 0}
                                            </h4>
                                            <small className="text-muted">{t('Total Refunds')}</small>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Shift Statistics Table */}
                                <div className="mt-4">
                                    <h6 className="mb-3 d-flex align-items-center">
                                        <Info className="me-2" size={18} />
                                        {t('Shift Statistics')}
                                    </h6>
                                    <Table responsive bordered className="table-hover">
                                        <tbody>
                                            <tr>
                                                <td className="fw-bold" style={{ width: '35%' }}>{t('Total Sales Count')}</td>
                                                <td>
                                                    <Badge color="info" pill>
                                                        {shiftHistory.totalSalesCount || 0}
                                                    </Badge>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">{t('Total Refund Count')}</td>
                                                <td>
                                                    <Badge color="warning" pill>
                                                        {shiftHistory.totalRefundCount || 0}
                                                    </Badge>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">{t('Opening Amount')}</td>
                                                <td>
                                                    {renderAllCurrencies(shiftHistory.openingAmount)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">{t('Closing Amount')}</td>
                                                <td>
                                                    {renderAllCurrencies(shiftHistory.closingAmount)}
                                                </td>
                                            </tr>
                                            {/* <tr>
                                                <td className="fw-bold">{t('Total Sales Amount')}</td>
                                                <td>
                                                    {renderAllCurrencies(shiftHistory.totalSalesAmount)}
                                                </td>
                                            </tr> */}
                                            <tr>
                                                <td className="fw-bold">{t('System Calculated Amount')}</td>
                                                <td>
                                                    {renderAllCurrencies(shiftHistory.totalSalesSystemCalculatedAmount)}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold">{t('Cash Difference')}</td>
                                                <td>
                                                    {renderCashDifference(shiftHistory.cashDifference) || <span className="text-muted">N/A</span>}
                                                </td>
                                            </tr>
                                            {/* <tr>
                                                <td className="fw-bold">{t('Total Amount (All Currencies)')}</td>
                                                <td className="fw-bold text-success">
                                                    {renderTotalAmount(shiftHistory.totalSalesAmount)}
                                                </td>
                                            </tr> */}
                                            <tr>
                                                <td className="fw-bold">{t('Shift Note')}</td>
                                                <td>
                                                    <div className={`alert ${shiftHistory.note ? 'alert-info' : 'alert-light'} mb-0`}>
                                                        {shiftHistory.note || t('No notes available')}
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Cashier Information Card */}
                    <Col lg={4}>
                        <Card className="mb-4 shadow-sm">
                            <CardHeader className="bg-primary text-white">
                                <h6 className="mb-0 d-flex align-items-center">
                                    <User className="me-2" size={20} />
                                    {t('Cashier Information')}
                                </h6>
                            </CardHeader>
                            <CardBody>
                                <div className="text-center mb-3">
                                    {cashierInfo.contact_person?.profile_picture ? (
                                        // <img
                                        //     src={`${imageBaseURL}${cashierInfo.contact_person.profile_picture}`}
                                        //     alt={cashierInfo.contact_person.full_name}
                                        //     className="rounded-circle border"
                                        //     width="100"
                                        //     height="100"
                                        //     style={{ objectFit: 'cover' }}
                                        //     onError={(e) => {
                                        //         e.target.style.display = 'none';
                                        //         e.target.parentElement.querySelector('.avatar-placeholder').style.display = 'flex';
                                        //     }}
                                        // />
                                        <S3ImageLoader
                                            className="m-0 rounded-circle border"
                                            style={{ objectFit: 'cover' }}
                                            width="100"
                                            height="100"
                                            fileName={cashierInfo.contact_person.profile_picture}
                                            alt="profile"
                                            fallbackSrc={errorImage}
                                        />
                                    ) : (
                                        <div className={`rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto ${cashierInfo.contact_person?.profile_picture ? 'avatar-placeholder' : ''}`}
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                display: cashierInfo.contact_person?.profile_picture ? 'none' : 'flex'
                                            }}>
                                            <User size={48} className="text-muted" />
                                        </div>
                                    )}
                                    <h5 className="mt-3 mb-0">{cashierInfo.contact_person?.full_name || cashierInfo.contact_person?.first_name + " " + cashierInfo.contact_person?.last_name || t('Unknown Cashier')}</h5>
                                    <p className="text-muted">{cashierInfo.email}</p>
                                    <Badge color={cashierInfo.status === 'active' ? 'success' : 'danger'} pill>
                                        {t(cashierInfo.status)}
                                    </Badge>
                                </div>

                                <div className="cashier-details">
                                    <h6 className="border-bottom pb-2 mb-3">{t('Contact Details')}</h6>
                                    <div className="detail-item mb-2">
                                        <small className="text-muted d-block">{t('Mobile')}</small>
                                        <p className="mb-0">
                                            {cashierInfo.contact_person?.phone_code || '91'} {cashierInfo.contact_person?.mobile || 'N/A'}
                                        </p>
                                    </div>
                                    <div className="detail-item mb-2">
                                        <small className="text-muted d-block">{t('Address')}</small>
                                        <p className="mb-0">{cashierInfo.contact_person?.address || 'N/A'}</p>
                                    </div>
                                    <div className="detail-item mb-2">
                                        <small className="text-muted d-block">{t('City')}</small>
                                        <p className="mb-0">{cashierInfo.contact_person?.city || 'N/A'}</p>
                                    </div>

                                    {/* <h6 className="border-bottom pb-2 mb-3 mt-3">{t('Account Status')}</h6>
                                    <div className="detail-item mb-2">
                                        <small className="text-muted d-block">{t('Status')}</small>
                                        <Badge color={cashierInfo.status === 'active' ? 'success' : 'danger'} pill>
                                            {t(cashierInfo.status)}
                                        </Badge>
                                    </div>
                                    <div className="detail-item mb-2">
                                        <small className="text-muted d-block">{t('Role')}</small>
                                        <Badge color="primary" pill className="text-capitalize">
                                            {cashierInfo.role || t('Unknown')}
                                        </Badge>
                                    </div> */}

                                    <h6 className="border-bottom pb-2 mb-3 mt-3">{t('Allowed Currencies')}</h6>
                                    <div className="detail-item mb-2">
                                        <small className="text-muted d-block">{t('Available Currencies')}</small>
                                        <div className="d-flex flex-wrap gap-1 mt-1">
                                            {allowedCurrencies.map(currencyCode => (
                                                <Badge key={currencyCode} color="light" className="text-dark border me-1">
                                                    {currencyCode}: {getCurrencySymbol(currencyCode)}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>

                                    {/* {cashierInfo.device_info?.mobile?.platform && (
                                        <>
                                            <h6 className="border-bottom pb-2 mb-3 mt-3">{t('Device Info')}</h6>
                                            <div className="detail-item mb-2">
                                                <small className="text-muted d-block">{t('Platform')}</small>
                                                <p className="mb-0 d-flex align-items-center">
                                                    <Smartphone className="me-2" size={16} />
                                                    {cashierInfo.device_info.mobile.platform}
                                                </p>
                                            </div>
                                        </>
                                    )} */}
                                </div>
                            </CardBody>
                        </Card>

                        {/* Quick Stats Card */}
                        <Card className="shadow-sm">
                            <CardHeader>
                                <h6 className="mb-0 d-flex align-items-center">
                                    <Calendar className="me-2" size={20} />
                                    {t('Timestamps')}
                                </h6>
                            </CardHeader>
                            <CardBody>
                                <div className="timestamp-list">
                                    <div className="timestamp-item mb-2">
                                        <small className="text-muted d-block">{t('Shift Started')}</small>
                                        <p className="mb-0 small">
                                            {formatDateTime(shiftHistory.shiftStartTime)}
                                        </p>
                                    </div>
                                    <div className="timestamp-item mb-2">
                                        <small className="text-muted d-block">{t('Shift Ended')}</small>
                                        <p className="mb-0 small">
                                            {shiftHistory.shiftEndTime
                                                ? formatDateTime(shiftHistory.shiftEndTime)
                                                : <Badge color="warning" size="sm">{t('Ongoing')}</Badge>
                                            }
                                        </p>
                                    </div>
                                    <div className="timestamp-item mb-2">
                                        <small className="text-muted d-block">{t('Created At')}</small>
                                        <p className="mb-0 small">
                                            {formatDateTime(shiftHistory.createdAt)}
                                        </p>
                                    </div>
                                    <div className="timestamp-item">
                                        <small className="text-muted d-block">{t('Last Updated')}</small>
                                        <p className="mb-0 small">
                                            {formatDateTime(shiftHistory.updatedAt)}
                                        </p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
};

export default ShiftRecordDetails;